// src/features/executive/insight/widgets/NewVsExistingDonut.tsx
import { Chip, Stack, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import { useTranslation } from 'react-i18next'
import CategoricalDonut from './CategoricalDonut'
import { useNewVsExisting } from '../hooks/use-new-vs-existing'

const COLOR: Record<string, string> = {
  new: '#2a8d9c',
  existing: '#e67e22',
}

// Absolute-level thresholds for the acquisition rate. 30% is a strong
// new-patient ratio for an established Thai dental clinic; below 10% means
// the clinic is leaning entirely on its existing base.
const ACQUISITION_HIGH_PCT = 30
const ACQUISITION_LOW_PCT = 10

// Period-over-period shift threshold in percentage points (not % growth):
// "rate moved from 20% → 25%" = +5pp. 5pp is significant for a metric that
// hovers in the 10–30% range; below this we stay silent.
const SHIFT_MIN_PP = 5

export default function NewVsExistingDonut() {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = useNewVsExisting()

  const total = data?.total ?? 0
  const previousTotal = data?.previousTotal ?? null
  const newCount = data?.newCount ?? 0
  const existingCount = data?.existingCount ?? 0
  const previousNewCount = data?.previousNewCount ?? null
  const previousExistingCount = data?.previousExistingCount ?? null
  const newRate = data?.newRate ?? 0
  const previousNewRate = data?.previousNewRate ?? null

  const segments = data
    ? [
        {
          key: 'new',
          label: t('EXECUTIVE.INSIGHT.NEW_VS_EXISTING.NEW'),
          count: newCount,
          percent: total > 0 ? (newCount / total) * 100 : 0,
          previousCount: previousNewCount,
        },
        {
          key: 'existing',
          label: t('EXECUTIVE.INSIGHT.NEW_VS_EXISTING.EXISTING'),
          count: existingCount,
          percent: total > 0 ? (existingCount / total) * 100 : 0,
          previousCount: previousExistingCount,
        },
      ]
    : []

  // Acquisition rate level — strong / weak signal in absolute terms.
  const newPct = newRate * 100
  const showStrong = total > 0 && newPct >= ACQUISITION_HIGH_PCT
  const showWeak = total > 0 && newPct < ACQUISITION_LOW_PCT

  // Acquisition rate shift — percentage-point change vs prev period.
  // Distinct from a relative % move; "20% → 25%" is +5pp, not +25%.
  const shiftPp =
    previousNewRate != null ? (newRate - previousNewRate) * 100 : null
  const showShiftUp = shiftPp != null && shiftPp >= SHIFT_MIN_PP
  const showShiftDown = shiftPp != null && shiftPp <= -SHIFT_MIN_PP

  const notice = (showStrong || showWeak || showShiftUp || showShiftDown) ? (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {showStrong && (
        <Chip
          icon={<CheckCircleOutlineIcon />}
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {t(
                'EXECUTIVE.INSIGHT.NEW_VS_EXISTING.ACQUISITION_STRONG',
                'อัตราการได้คนไข้ใหม่สูง: คนไข้ใหม่ {{pct}}% ของกลุ่มทั้งหมด',
                { pct: newPct.toFixed(0) }
              )}
            </Typography>
          }
          color="success"
          variant="outlined"
          size="small"
          sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
        />
      )}
      {showWeak && (
        <Chip
          icon={<WarningAmberOutlinedIcon />}
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {t(
                'EXECUTIVE.INSIGHT.NEW_VS_EXISTING.ACQUISITION_WEAK',
                'อัตราการได้คนไข้ใหม่ต่ำ: คนไข้ใหม่เพียง {{pct}}% — แนะนำให้รักษาฐานคนไข้เดิมเป็นหลัก',
                { pct: newPct.toFixed(0) }
              )}
            </Typography>
          }
          color="warning"
          variant="outlined"
          size="small"
          sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
        />
      )}
      {showShiftUp && (
        <Chip
          icon={<TrendingUpIcon />}
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {t(
                'EXECUTIVE.INSIGHT.NEW_VS_EXISTING.SHIFT_UP',
                'คนไข้ใหม่เพิ่มขึ้นเป็น {{cur}}% (จาก {{prev}}% เดิม)',
                {
                  cur: newPct.toFixed(0),
                  prev: ((previousNewRate ?? 0) * 100).toFixed(0),
                }
              )}
            </Typography>
          }
          color="info"
          variant="outlined"
          size="small"
          sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
        />
      )}
      {showShiftDown && (
        <Chip
          icon={<TrendingDownIcon />}
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {t(
                'EXECUTIVE.INSIGHT.NEW_VS_EXISTING.SHIFT_DOWN',
                'คนไข้ใหม่ลดลงเหลือ {{cur}}% (จาก {{prev}}% เดิม)',
                {
                  cur: newPct.toFixed(0),
                  prev: ((previousNewRate ?? 0) * 100).toFixed(0),
                }
              )}
            </Typography>
          }
          color="warning"
          variant="outlined"
          size="small"
          sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
        />
      )}
    </Stack>
  ) : null

  return (
    <CategoricalDonut
      title={t('EXECUTIVE.INSIGHT.NEW_VS_EXISTING.TITLE')}
      titleTooltip={t(
        'EXECUTIVE.INSIGHT.NEW_VS_EXISTING.SOURCE_TOOLTIP',
        '"คนไข้ใหม่" = patients ที่ first-EVER visit อยู่ในช่วงเวลาที่เลือก (นับทั้งเครือ ทุกสาขา)\n"คนไข้เก่า" = เคยมาก่อนช่วงเวลาที่เลือก\n\nต่างจาก #10 New Patient Trend ที่นับ first-at-branch (รวมเคสหยิบแฟ้มข้ามสาขา) — widget นี้นับ true acquisition ในเครือ\n\nKPI หลัก: Acquisition Rate = คนไข้ใหม่ ÷ cohort ทั้งหมด'
      )}
      emptyMessage={t(
        'EXECUTIVE.INSIGHT.NEW_VS_EXISTING.EMPTY',
        'ไม่พบคนไข้ในช่วงเวลาและเงื่อนไขที่เลือก'
      )}
      notice={notice}
      segments={segments}
      total={total}
      previousTotal={previousTotal}
      colorMap={COLOR}
      centerLabel={t('EXECUTIVE.INSIGHT.TOTAL_PATIENTS')}
      unit={t('EXECUTIVE.INSIGHT.UNIT.PATIENT')}
      loading={loading}
      error={error}
      onRetry={refetch}
    />
  )
}
